/* eslint-disable */
import React, { PureComponent } from 'react'
import { withRouter } from 'react-router-dom'
import { InView } from 'react-intersection-observer'
import PropTypes from 'prop-types'
import { get, isEqual, find, cloneDeep } from 'lodash'
import StyledAppNexusPlacement, { DummyComponent } from './StyledAppNexusPlacement'
import { setPageOptions } from './../AstUtils'
import NativeAd from './NativeAd'
import Viewable from 'components/Viewable'
import impressionTracking from './impressionTracking'
import { setFullscreenLimitCookie, getFullscreenLimitCookie } from './cookie'
import {
  getApn,
  resizeSSP,
  disposeAd,
  showTag,
  revokeMediationListeners,
  scaleCreative,
  getCreative,
  supportsFullscreen,
  mediationEvents,
  resizeAd,
} from './../AstUtils'

const lazyloadWhitelist = ['placement_native', 'native_device', 'vi_panorama_bottom']

class AppNexusPlacement extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      intersected: lazyloadWhitelist.indexOf(props.id) !== -1,
      impressionSuspended: true,
      available: false,
      native: {
        isNative: false,
        dataObj: null,
        isWallpaper: false,
      },
      loaded: false,
      inScreen: false,
      isFullscreen: false,
    }

    /*
     * This property is used to set intersection config and temporary inject
     * 3x3 sizes for the tag supporting fullscreen scroll.
     * It does not decide which one actually is targeted. That is done by
     * this.isActiveFullscreenPlacement
     */

    const { id } = this.props
    this.intersectionConfig = {
      threshold: supportsFullscreen(id) ? 0.3 : 1,
      triggerOnce: supportsFullscreen(id) ? false : true,
    }
    this.attachMediationListeners = this.attachMediationListeners.bind(this)
    this.onIntersectionChange = this.onIntersectionChange.bind(this)
    this.loadPlacements = this.loadPlacements.bind(this)
    this.handleObserve = this.handleObserve.bind(this)
    this.onViewableImpression = this.onViewableImpression.bind(this)
  }

  /*
   * Handle height dimension mutations by Google SSP backfill placements
   * where creative dimensions do not honor the placement dimensions
   */
  handleObserve(mutationList) {
    // get last mutation
    const mut = mutationList[mutationList.length - 1].target
    resizeSSP(mut)
    // Special case of SSP not selling and appnexus backfill loading a wallpaper
    if (mut.clientHeight === 900) {
      /* wallpaper disable-cogwheel */
      this.setState({
        isWallpaper: true,
      })
      return
    }
  }

  componentDidMount() {
    const { id, history, inventory } = this.props
    this.mounted = true
    const inventoryData = cloneDeep(inventory)
    this.loadPlacements(inventoryData)
    this.unlisten = history.listen((location) => {
      const newlocation = get(location, 'pathname')
      const prevLocation = get(this.props, 'location.pathname')
      if (newlocation !== prevLocation) {
        disposeAd(id)
      }
    })
    // mutation observer for catching magic iframe resizes
    this.mutationObserver = new MutationObserver(this.handleObserve)
    // temporary impression tracking object used for coming A/B test of fullscreenscroll banner
    this.trackImpression = impressionTracking(id)
  }

  componentDidUpdate(prevProps) {
    const { inventory, id } = this.props
    const inventoryData = cloneDeep(inventory)
    const oldInventoryData = cloneDeep(prevProps.inventory)
    if (!isEqual(oldInventoryData, inventoryData)) {
      this.loadPlacements(inventoryData)
    }
    if (this.mounted && this.state.intersected && this.state.available) {
      showTag(id)
    }
  }

  componentWillUnmount() {
    const { id } = this.props
    disposeAd(id)
    this.unlisten()
    if (this.mutationObserver) this.mutationObserver.disconnect()
    this.mounted = false
  }

  /**
   * Read debug paramers from URL starts with ast_
   * configure them to apn tag
   */

  attachMediationListeners(id) {
    const { apnTag, apnQueue } = getApn()
    apnQueue.push(() => {
      mediationEvents.map((eventType) =>
        apnTag.onEvent(eventType, id, this.onMediation.bind(this, eventType)),
      )
    })
  }

  /* eslint-disable camelcase */
  loadPlacements(inventory = {}) {
    const { apnTag, apnQueue } = getApn()
    const { id, safeframe, loggedIn, device } = this.props
    const { placements } = inventory
    const placement = find(placements, (itr) => get(itr, 'appnexus.target_id') === id)
    const pageOptions = {
      inventory,
      loggedIn,
      device: !device.is('medium') ? 'mobile' : 'desktop',
    }

    if (placement && placement.vendor === 'appnexus') {
      setPageOptions(pageOptions)
      const {
        appnexus: { member, target_id, inventory_code, allowed_formats, sizes, keywords = {} },
      } = placement

      this.attachMediationListeners(id)

      /*
       * This keyword together with 3x3 size triggers the fullscreen scroll ad for specific tag
       * - target against explicit name as only one placement should have fullscreen at a time
       */
      const tagKeywords = Object.assign({}, keywords)
      const hasFullscreenCookie = getFullscreenLimitCookie()
      const isFullscreenAd = supportsFullscreen(id) && !hasFullscreenCookie
      const adFormat = tagKeywords['se-generic-adformat']

      if (adFormat && isFullscreenAd) {
        tagKeywords['se-generic-adformat'] = [...adFormat, 'fullscreen']
      }
      const tagSizes = JSON.parse(sizes)

      let tag = {
        member,
        position: 'above',
        invCode: inventory_code,
        targetId: target_id,
        sizes: tagSizes,
        allowedFormats: allowed_formats,
        keywords: tagKeywords,
        supplyType: !device.is('medium') ? 'mobile_web' : 'web',
      }

      const withSafeframe = {
        enableSafeFrame: true,
        safeframeConfig: {
          allowExpansionByPush: true,
          allowExpansionByOverlay: true,
        },
      }
      const sfConfig = safeframe ? withSafeframe : {}
      tag = Object.assign(tag, sfConfig)

      apnQueue.push(() => {
        apnTag.defineTag(tag)
      })

      setTimeout(() => {
        apnQueue.push(() => {
          apnTag.loadTags()
        })
      }, 0)
    }
  }

  onIntersectionChange(inView, { isIntersecting, intersectionRatio }) {
    if (
      !this.state.intersected &&
      inView &&
      isIntersecting &&
      intersectionRatio >= this.intersectionConfig.threshold &&
      this.mounted
    ) {
      this.setState({
        ...this.state,
        intersected: true,
        inScreen: true,
      })
    }

    if (inView !== this.state.inScreen) {
      this.setState({ inScreen: inView })
    }
  }

  adLoaded({ targetId, height = 0, width = 0 }) {
    this.setState({ loaded: true })
    if (this.trackImpression.isTracked()) {
      this.setState({ impressionSuspended: false })
      this.trackImpression.send('loaded')
    }
    const placementNode = document.getElementById(targetId)

    const creative = getCreative(placementNode)

    if (height === 1 && width === 1) {
      placementNode.style.height = '100%'
      resizeAd(targetId)
    }
    const creativeIframe = creative.getElementsByTagName('iframe')[0]
    // Attach mutation observer to deal with Google SSP backfills
    this.mutationObserver.observe(creativeIframe, {
      attributes: true,
      attributeFilter: ['height', 'width'],
      attributeOldValue: true,
    })

    placementNode.classList.add('creative-loaded')
    if (targetId.includes('placement_add-on') || targetId.includes('infor_kopet')) {
      if (creativeIframe) {
        placementNode.style.width = 'auto'
        resizeAd(targetId)
      }
    }
    // get placementNode
    if (this.props.scaleContent && height !== 900) {
      scaleCreative(placementNode, creative)
    }
  }

  adAvailable({ targetId, adType, height = 0, width = 0, native }) {
    this.setState({
      ...this.state,
      available: true,
      isWallpaper: height === 900,
      isFullscreen: height === 3 && width === 3,
    })

    const placement = document.getElementById(targetId)
    if (width === 2 && height === 1) {
      placement.style.display = 'none'
    } else if (height === 900) {
      placement.style.width = width + 'px'
      window.document.body.style.overflowX = 'hidden'
      placement.style.cssText = ''
      // wallpaper
    } else if (adType === 'native') {
      this.setState({
        ...this.state,
        native: {
          isNative: true,
          dataObj: native,
        },
      })
      placement.style.height = 'auto'
      placement.style.width = 'auto'
      placement.classList.add('native')
    } else {
      placement.style.width = '100%'
      placement.style.height = height !== 3 ? height + 'px' : '86vh'
      placement.style.margin = 'auto'
    }
    if (this.trackImpression.isTracked()) {
      this.trackImpression.setCreative(adObj.tagId)
      this.trackImpression.setVariant(height === 3 && width === 3 ? 'fullscreen' : 'standard')
    }
  }

  adRequested(eventType) {
    this.props.onMediation && this.props.onMediation(eventType)
    setFullscreenLimitCookie(this.props.id)
  }

  onMediation(eventType, adObj) {
    const { targetId } = adObj
    if (!targetId) {
      return
    }

    if (this.props.onMediation) {
      this.props.onMediation(eventType, adObj)
    }

    switch (eventType) {
      case 'adRequested':
        this.adRequested(eventType)
        break
      case 'adError':
      case 'adBadRequest':
      case 'adRequestFailure':
        break
      case 'adNoBid':
        revokeMediationListeners(targetId)
        break
      case 'adAvailable':
        this.adAvailable(adObj)
        break
      case 'adLoaded':
        this.adLoaded(adObj)
        break
      default:
        break
    }
  }

  /**
   * Trigger in-view impression and suspend the viewable trigger
   * used for tracking impressions during the test phase of full
   * screen placement creatives.
   */
  onViewableImpression() {
    this.setState({ impressionSuspended: true }, this.trackImpression.send('in-view'))
  }

  render() {
    const { path, id, device } = this.props
    const isIndexPanorama = path === 'index' && id === 'placement_panorama'
    const isStickyPlacement = id === 'placement_widescreen-sticky'
    let rootMargin = '400px 0px 400px 0px'
    if (isIndexPanorama || isStickyPlacement) {
      rootMargin = '0px 0px 0px 0px'
    } else if (id === 'placement_hero') {
      rootMargin = '0px 0px 100px 0px'
    }

    const options = {
      onChange: this.onIntersectionChange,
      threshold: [this.intersectionConfig.threshold],
      rootMargin,
      triggerOnce: this.intersectionConfig.triggerOnce,
    }

    const {
      native: { isNative, dataObj },
      available,
      isWallpaper,
      intersected,
      impressionSuspended,
      inScreen,
      isFullscreen,
      loaded,
    } = this.state

    if (available) {
      return (
        <InView {...options} as="div" onChange={this.onIntersectionChange}>
          <div className="blocket-placement">
            {isNative && intersected ? (
              <div id={this.props.id}>
                <NativeAd ad={dataObj} device={device} />
              </div>
            ) : (
              <Viewable onViewable={this.onViewableImpression} suspend={impressionSuspended}>
                <StyledAppNexusPlacement
                  className={[
                    'placement',
                    !this.props.cogwheel ? 'disable-cogwheel' : '',
                    isWallpaper ? 'wallpaper disable-cogwheel' : '',
                    loaded ? 'creative-loaded' : '',
                    isFullscreen ? 'fullscreen' : 'standard',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ ...this.props.style }}
                  id={this.props.id}
                  name={this.props.path}
                  inScreen={inScreen}
                  fullscreen={isFullscreen}
                >
                  {this.props.cogwheel && (
                    <div id={`${this.props.id}-button`} className="cogwheel" />
                  )}
                </StyledAppNexusPlacement>
              </Viewable>
            )}
            <DummyComponent isWallpaper={isWallpaper} />
          </div>
        </InView>
      )
    }
    return <div className="blocket-placement" style={{ ...this.props.style }} id={this.props.id} />
  }
}

AppNexusPlacement.defaultProps = {
  cogwheel: true,
  safeframe: true,
  scaleContent: true,
  inventory: {},
}

AppNexusPlacement.propTypes = {
  /**
   * Data for this placement component
   * placement inventory either from bff api or marketing api
   */
  inventory: PropTypes.object,

  /**
   * id of the dom element where placement banner should be attached
   */
  id: PropTypes.string.isRequired,

  /**
   * Scale creative to the size of the placement when the scaling factor is larger than 1,
   * ie: when the creative is larger than the placement
   */
  scaleContent: PropTypes.bool,

  /**
   * Show cogwheel
   */
  cogwheel: PropTypes.bool,

  /**
   * Use Safeframe
   */
  safeframe: PropTypes.bool,

  /**
   * Custom  styles
   */
  style: PropTypes.object,

  /**
   * Callback for any mediation events for the ad
   */
  onMediation: PropTypes.func,
}

export default withRouter(AppNexusPlacement)
